import React, { useCallback, useEffect, useRef, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import ReportUserDialog from "@/components/ReportUserDialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Video, VideoOff, Mic, MicOff, SkipForward, Flag, Ban, Loader2, Users, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl, formatApiError } from "@/lib/api";
import { useRealtime } from "@/context/RealtimeContext";
import { useLanguage } from "@/context/LanguageContext";

export default function RandomVideoPage() {
  const { t } = useLanguage();
  const { subscribe, send, connected } = useRealtime();
  const [status, setStatus] = useState("idle");
  const [peer, setPeer] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [reportOpen, setReportOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [previewReady, setPreviewReady] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const localVideo = useRef(null);
  const remoteVideo = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const previewStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const sessionRef = useRef(null);
  const activeRef = useRef(true);
  const pendingSignals = useRef([]);
  const queueTimer = useRef(null);

  const cleanupMedia = useCallback(() => {
    try { pcRef.current?.close(); } catch {}
    pcRef.current = null;
    localStreamRef.current?.getTracks().forEach((track) => track.stop());
    localStreamRef.current = null;
    remoteStreamRef.current = null;
    if (localVideo.current) localVideo.current.srcObject = previewStreamRef.current || null;
    if (remoteVideo.current) remoteVideo.current.srcObject = null;
    pendingSignals.current = [];
    sessionRef.current = null;
  }, []);

  useEffect(() => {
    if (!window.matchMedia("(min-width: 1024px)").matches) return undefined;
    if (!navigator.mediaDevices?.getUserMedia) {
      setPreviewError("Camera preview is not supported by this browser.");
      return undefined;
    }

    let cancelled = false;
    navigator.mediaDevices.getUserMedia({
      audio: false,
      video: { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30, max: 30 } },
    }).then((stream) => {
      if (cancelled) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      previewStreamRef.current = stream;
      setPreviewReady(true);
      setPreviewError("");
      if (localVideo.current) {
        localVideo.current.srcObject = stream;
        localVideo.current.play().catch(() => {});
      }
    }).catch((e) => {
      if (!cancelled) setPreviewError(e?.name === "NotAllowedError" ? "Allow camera access to see your preview." : "Camera preview is unavailable.");
    });

    return () => {
      cancelled = true;
      previewStreamRef.current?.getTracks().forEach((track) => track.stop());
      previewStreamRef.current = null;
    };
  }, []);

  const applySignal = useCallback(async (evt) => {
    const pc = pcRef.current;
    if (evt.session_id !== sessionRef.current) return;
    if (!pc) {
      pendingSignals.current.push(evt);
      return;
    }
    try {
      if (evt.kind === "offer") {
        await pc.setRemoteDescription(new RTCSessionDescription(evt.payload));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        send({ type: "random:signal", session_id: evt.session_id, kind: "answer", payload: answer });
        for (const queued of pendingSignals.current.splice(0)) {
          if (queued.kind === "ice") await pc.addIceCandidate(queued.payload);
        }
      } else if (evt.kind === "answer") {
        await pc.setRemoteDescription(new RTCSessionDescription(evt.payload));
        for (const queued of pendingSignals.current.splice(0)) {
          if (queued.kind === "ice") await pc.addIceCandidate(queued.payload);
        }
      } else if (evt.kind === "ice") {
        if (pc.remoteDescription) await pc.addIceCandidate(evt.payload);
        else pendingSignals.current.push(evt);
      }
    } catch (e) {
      console.error("Random video signaling error", e);
    }
  }, [send]);

  const startPeer = useCallback(async (match, initiator) => {
    cleanupMedia();
    setPeer(match.peer);
    setSessionId(match.session_id);
    sessionRef.current = match.session_id;
    setStatus("connecting");
    setError("");

    try {
      const cameraPreview = previewStreamRef.current?.getVideoTracks()[0];
      const [{ data: ice }, audioStream] = await Promise.all([
        api.get("/calls/ice-config"),
        navigator.mediaDevices.getUserMedia({ audio: true, video: !cameraPreview }),
      ]);

      let stream;
      if (cameraPreview) {
        const audioTrack = audioStream.getAudioTracks()[0];
        stream = new MediaStream([cameraPreview.clone(), audioTrack].filter(Boolean));
        audioStream.getVideoTracks().forEach((track) => track.stop());
      } else {
        stream = audioStream;
      }

      localStreamRef.current = stream;
      setMicOn(true);
      setCamOn(true);
      if (localVideo.current) localVideo.current.srcObject = stream;

      const pc = new RTCPeerConnection({ iceServers: ice.iceServers || [] });
      pcRef.current = pc;
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));
      const remote = new MediaStream();
      remoteStreamRef.current = remote;
      pc.ontrack = (event) => {
        if (!remote.getTracks().some((track) => track.id === event.track.id)) remote.addTrack(event.track);
        if (remoteVideo.current) {
          remoteVideo.current.srcObject = remote;
          remoteVideo.current.play().catch(() => {});
        }
        setStatus("active");
      };
      pc.onicecandidate = (event) => {
        if (event.candidate && sessionRef.current) {
          send({ type: "random:signal", session_id: sessionRef.current, kind: "ice", payload: event.candidate });
        }
      };
      pc.onconnectionstatechange = () => {
        if (["failed", "closed"].includes(pc.connectionState)) {
          setStatus("idle");
          cleanupMedia();
        }
      };

      const queued = pendingSignals.current.splice(0);
      for (const signal of queued) await applySignal(signal);

      if (initiator) {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        send({ type: "random:signal", session_id: match.session_id, kind: "offer", payload: offer });
      }
    } catch (e) {
      cleanupMedia();
      setStatus("idle");
      setPeer(null);
      setSessionId(null);
      const message = e?.name === "NotAllowedError"
        ? "Camera and microphone permission is required for random video chat."
        : e?.message || "Could not start video chat.";
      setError(message);
      toast.error(message);
      try { await api.post("/random-video/leave"); } catch {}
    }
  }, [applySignal, cleanupMedia, send]);

  const joinQueue = useCallback(async () => {
    if (!connected || busy || status === "waiting" || status === "connecting" || status === "active") return;
    setBusy(true);
    setError("");
    setPeer(null);
    setSessionId(null);
    cleanupMedia();
    setStatus("waiting");
    try {
      const { data } = await api.post("/random-video/queue");
      if (data.matched) await startPeer(data, true);
    } catch (e) {
      setStatus("idle");
      setError(formatApiError(e.response?.data?.detail));
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  }, [busy, cleanupMedia, connected, startPeer, status]);

  const next = useCallback(async () => {
    if (queueTimer.current) clearTimeout(queueTimer.current);
    setBusy(true);
    try { await api.post("/random-video/skip"); } catch {}
    cleanupMedia();
    setPeer(null);
    setSessionId(null);
    setStatus("idle");
    setBusy(false);
    queueTimer.current = setTimeout(() => { if (activeRef.current) joinQueue(); }, 250);
  }, [cleanupMedia, joinQueue]);

  useEffect(() => {
    activeRef.current = true;
    const unsubscribe = subscribe((evt) => {
      if (evt.type === "random:matched") {
        startPeer({ session_id: evt.session_id, peer: evt.peer }, false);
      } else if (evt.type === "random:signal") {
        applySignal(evt);
      } else if (evt.type === "random:ended" && evt.session_id === sessionRef.current) {
        cleanupMedia();
        setPeer(null);
        setSessionId(null);
        setStatus("idle");
        toast(t("The other person left. Finding someone else…"));
        queueTimer.current = setTimeout(() => { if (activeRef.current) joinQueue(); }, 300);
      }
    });
    return () => unsubscribe();
  }, [applySignal, cleanupMedia, joinQueue, startPeer, subscribe, t]);

  useEffect(() => {
    return () => {
      activeRef.current = false;
      if (queueTimer.current) clearTimeout(queueTimer.current);
      api.post("/random-video/leave").catch(() => {});
      cleanupMedia();
    };
  }, [cleanupMedia]);

  const toggleMic = () => {
    const track = localStreamRef.current?.getAudioTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    setMicOn(track.enabled);
  };

  const toggleCam = () => {
    const track = localStreamRef.current?.getVideoTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    setCamOn(track.enabled);
  };

  const block = async () => {
    if (!peer?.username) return;
    try {
      await api.post("/blocks", { username: peer.username });
      toast.success(t("User blocked"));
      await next();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const initials = (peer?.username || "?").charAt(0).toUpperCase();

  return (
    <AppShell>
      <div className="mx-auto w-full max-w-6xl">
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-pink-500" />
              <h1 className="font-heading text-2xl font-extrabold text-slate-900 sm:text-3xl">{t("Random Video Chat")}</h1>
            </div>
            <p className="mt-1 text-sm text-slate-500">Meet a random Yaprio member. You can skip, report, or block at any time.</p>
          </div>
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-400"><Users className="h-4 w-4" /> {connected ? "Connected" : "Connecting…"}</div>
        </div>

        <div className="overflow-hidden rounded-[28px] border border-white/70 bg-slate-950 shadow-[0_24px_70px_rgba(15,23,42,0.16)] ring-1 ring-slate-900/5">
          <div className="relative aspect-video min-h-[360px] w-full overflow-hidden bg-slate-950">
            {status === "active" || status === "connecting" ? (
              <>
                <video ref={remoteVideo} autoPlay playsInline className="h-full w-full object-cover" />
                {status === "connecting" && <div className="absolute inset-0 flex items-center justify-center bg-slate-950/55"><div className="text-center text-white"><Loader2 className="mx-auto h-8 w-8 animate-spin" /><p className="mt-3 font-semibold">Connecting video…</p></div></div>}
                <div className="absolute bottom-4 right-4 h-28 w-40 overflow-hidden rounded-2xl border-2 border-white/70 bg-black shadow-lg sm:h-36 sm:w-52">
                  <video ref={localVideo} autoPlay muted playsInline className="h-full w-full object-cover" />
                </div>
                {peer && <div className="absolute left-4 top-4 flex items-center gap-2 rounded-full bg-black/45 px-3 py-2 text-sm font-semibold text-white backdrop-blur"><Avatar className="h-7 w-7"><AvatarImage src={mediaUrl(peer.avatar_url)} /><AvatarFallback className="bg-pink-500 text-xs font-bold text-white">{initials}</AvatarFallback></Avatar>@{peer.username}</div>}
              </>
            ) : (
              <div className="relative h-full min-h-[360px]">
                {previewReady ? (
                  <>
                    <video ref={localVideo} autoPlay muted playsInline className="h-full w-full object-cover" />
                    <div className="absolute inset-x-0 top-0 flex items-center justify-between p-5">
                      <div className="rounded-full bg-black/45 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur">Your camera preview</div>
                      <div className="rounded-full bg-black/45 px-3 py-1.5 text-xs font-medium text-white/80 backdrop-blur">Camera on · mic off</div>
                    </div>
                    <div className="absolute inset-x-0 bottom-0 flex flex-col items-center justify-end bg-gradient-to-t from-black/80 via-black/20 to-transparent px-6 pb-7 pt-20 text-center">
                      <h2 className="text-xl font-bold text-white sm:text-2xl">{status === "waiting" ? t("Looking for someone…") : t("Ready to meet someone new?")}</h2>
                      <p className="mt-2 text-sm text-white/75">{status === "waiting" ? "Keep this page open while we find a compatible member." : "Check your camera, then start when you’re ready."}</p>
                    </div>
                  </>
                ) : (
                  <div className="flex h-full min-h-[360px] items-center justify-center px-6 text-center text-white">
                    <div>
                      <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-pink-500/15"><Video className="h-9 w-9 text-pink-300" /></div>
                      <h2 className="mt-5 text-xl font-bold">{status === "waiting" ? t("Looking for someone…") : t("Camera preview unavailable")}</h2>
                      <p className="mt-2 max-w-md text-sm text-slate-300">{status === "waiting" ? "Keep this page open while we find a compatible member." : previewError || "You can still start random chat and allow camera access when prompted."}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 border-t border-white/5 bg-slate-950 px-5 py-4">
            {(status === "active" || status === "connecting") && <>
              <button type="button" onClick={toggleMic} className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20" title={micOn ? t("Mute microphone") : t("Unmute microphone")}>{micOn ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}</button>
              <button type="button" onClick={toggleCam} className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20" title={camOn ? t("Turn camera off") : t("Turn camera on")}>{camOn ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}</button>
              <Button onClick={next} disabled={busy} className="gap-2 rounded-full bg-pink-500 font-bold text-white hover:bg-pink-600"><SkipForward className="h-4 w-4" /> Next</Button>
              <Button onClick={() => setReportOpen(true)} variant="outline" className="gap-2 rounded-full border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"><Flag className="h-4 w-4" /> Report</Button>
              <Button onClick={block} variant="outline" className="gap-2 rounded-full border-red-400/30 bg-transparent text-red-300 hover:bg-red-500/10"><Ban className="h-4 w-4" /> Block</Button>
            </>}
            {status === "idle" && <Button onClick={joinQueue} disabled={!connected || busy} className="h-11 gap-2 rounded-full bg-pink-500 px-7 font-bold text-white shadow-lg shadow-pink-500/20 hover:bg-pink-600">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Video className="h-4 w-4" />} Start random chat</Button>}
            {status === "waiting" && <Button onClick={next} disabled={busy} variant="outline" className="gap-2 rounded-full border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"><SkipForward className="h-4 w-4" /> Cancel</Button>}
          </div>
        </div>

        {error && <p className="mt-3 text-center text-sm font-medium text-red-500">{error}</p>}
        <p className="mt-4 text-center text-xs text-slate-400">Random chat only matches members who have enabled random discovery. If someone makes you uncomfortable, use Report or Block.</p>
      </div>

      {peer?.username && (
        <ReportUserDialog
          open={reportOpen}
          onOpenChange={setReportOpen}
          username={peer.username}
          onSubmitted={() => {
            toast(t("Report submitted. Leaving this match for your safety."));
            next();
          }}
        />
      )}
    </AppShell>
  );
}
